'use client';

import { ReactNode, useRef, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';
import { Box, BoxProps } from '@mui/material';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import { IconButton } from '../buttons';

import 'swiper/css';
import 'swiper/css/navigation';

interface SwiperSliderProps extends BoxProps {
    elements: ReactNode[];
    slidesPerView?: number;
    spaceBetween?: number;
    breakpoints?: {
        [key: number]: {
            slidesPerView: number;
            spaceBetween?: number;
        };
    };
}

const SwiperSlider = ({
    elements,
    slidesPerView = 1,
    spaceBetween = 24,
    breakpoints,
    ...boxProps
}: SwiperSliderProps) => {
    const swiperRef = useRef<SwiperType | null>(null);
    const [isBeginning, setIsBeginning] = useState(true);
    const [isEnd, setIsEnd] = useState(false);

    const handlePrev = () => {
        swiperRef.current?.slidePrev();
    };

    const handleNext = () => {
        swiperRef.current?.slideNext();
    };

    const updateNavigationState = (swiper: SwiperType) => {
        setIsBeginning(swiper.isBeginning);
        setIsEnd(swiper.isEnd);
    };

    if (elements.length === 0) return null;

    return (
        <Box {...boxProps}>
            <Swiper
                modules={[Navigation]}
                slidesPerView={slidesPerView}
                spaceBetween={spaceBetween}
                breakpoints={breakpoints}
                centeredSlides={slidesPerView > 1}
                onSwiper={(swiper) => {
                    swiperRef.current = swiper;
                    updateNavigationState(swiper);
                }}
                onSlideChange={(swiper) => {
                    updateNavigationState(swiper);
                }}
            >
                {elements.map((element, index) => (
                    <SwiperSlide key={index} style={{ display: 'flex', justifyContent: 'center' }}>
                        {element}
                    </SwiperSlide>
                ))}
            </Swiper>
            <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', mt: 2 }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <IconButton
                        icon={<KeyboardArrowLeft />}
                        onClick={handlePrev}
                        disabled={isBeginning}
                    />
                    <IconButton
                        icon={<KeyboardArrowRight />}
                        onClick={handleNext}
                        disabled={isEnd}
                    />
                </Box>
            </Box>
        </Box>
    );
};

export { SwiperSlider };
